import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Fab, Snackbar, Alert, Avatar, Switch, Stack, Chip, LinearProgress } from '@mui/material';
import {
  addCreditsToTenant,
  fetchTenants,
  addAdminToTenant,
  fetchTenantAdmins,
  removeAdminFromTenant,
  refreshTenants,
  assignPlanToTenant,
  setTenantStatus,
  getTenantManagementCollectionNames,
  getTenantManagementMode,
  isCentralTenantManagementMode,
  setTenantManagementCollectionNames,
  createTenant,
} from './services/tenantService';
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db, storage } from './firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import TenantManagementHeader from './components/TenantManagementHeader';
import { getCookie } from './cookieUtils';
import {
  PLANS,
  PLAN_ORDER,
  buildLicenseForPlan,
  computeActiveUserLimit,
  computeCreditsRemaining,
  computeRolloverRemaining,
  isCycleExpired,
} from '../../utils/licenseUtils';
const DEFAULT_LICENSE_PLAN = 'Professional';
const LICENSE_PLAN_OPTIONS = (PLAN_ORDER || Object.keys(PLANS))
  .filter((plan) => PLANS[plan] && PLANS[plan].credits !== undefined);

const formatNumber = (value) => Number(value || 0).toLocaleString();

const getPlanNameFromTenant = (tenant) => tenant?.license?.plan || tenant?.plan || DEFAULT_LICENSE_PLAN;

const getLicenseForTenant = (tenant) => {
  const planName = getPlanNameFromTenant(tenant);
  const fallbackLicense = buildLicenseForPlan(planName, { planAssignedBy: 'system-fallback' });
  return tenant?.license || fallbackLicense;
};

const getLicenseSummary = (tenant) => {
  const license = getLicenseForTenant(tenant);
  const totalCredits = license?.currentCycle?.creditsTotal || 0;
  const usedCredits = license?.currentCycle?.creditsUsed || 0;
  const rolloverCredits = computeRolloverRemaining(license);
  const remainingCredits = computeCreditsRemaining(license);
  const cycleRemainingCredits = Math.max(0, totalCredits - usedCredits);
  const userLimit = computeActiveUserLimit(license, 0);

  return {
    license,
    plan: license?.plan || getPlanNameFromTenant(tenant),
    totalCredits,
    usedCredits,
    cycleRemainingCredits,
    rolloverCredits,
    remainingCredits,
    userLimit,
    cycleEnd: license?.currentCycle?.cycleEnd || null,
    expired: isCycleExpired(license),
  };
};

const getPlanChipColor = (planName) => {
  const color = PLANS[planName]?.color || '#94a3b8';
  return {
    color,
    backgroundColor: `${color}22`,
    borderColor: `${color}55`,
  };
};

const parseSuperAdminEmails = (emailsFromProps) => {
  if (Array.isArray(emailsFromProps)) {
    return emailsFromProps.map((e) => (e || '').trim().toLowerCase()).filter(Boolean);
  }
  return [
    ...(process.env.REACT_APP_SUPERADMINS || '').split(','),
    ...(process.env.REACT_APP_SUPERADMIN_EMAILS || '').split(','),
  ]
    .map((e) => e.trim().toLowerCase())
    .filter((email, index, emails) => email && emails.indexOf(email) === index);
};

const normalizeImageUrl = (rawUrl) => {
  const input = (rawUrl || '').trim();
  if (!input) return '';

  try {
    const u = new URL(input);
    const host = u.hostname.toLowerCase();
    const path = u.pathname;

    // Unsplash photo page -> official download redirect endpoint
    const unsplashMatch = path.match(/\/photos\/[^/]*-([A-Za-z0-9_-]+)/);
    if (host.includes('unsplash.com') && unsplashMatch?.[1]) {
      return `https://unsplash.com/photos/${unsplashMatch[1]}/download?force=true&w=1200`;
    }

    // Google Drive share/open links -> direct view link
    const driveIdFromPath = path.match(/\/file\/d\/([^/]+)/)?.[1];
    const driveIdFromQuery = u.searchParams.get('id');
    const driveId = driveIdFromPath || driveIdFromQuery;
    if (host.includes('drive.google.com') && driveId) {
      return `https://drive.google.com/uc?export=view&id=${driveId}`;
    }

    // Dropbox share link -> raw link
    if (host.includes('dropbox.com')) {
      u.searchParams.set('raw', '1');
      u.searchParams.delete('dl');
      return u.toString();
    }

    return input;
  } catch {
    return input;
  }
};

const uploadExternalImageToStorage = async (inputUrl, tenantId) => {
  const normalizedUrl = normalizeImageUrl(inputUrl);
  if (!normalizedUrl) return '';

  try {
    const response = await fetch(normalizedUrl);
    if (!response.ok) throw new Error(`Image fetch failed with status ${response.status}`);

    const blob = await response.blob();
    const isImage = blob.type?.startsWith('image/');
    if (!isImage) {
      throw new Error('Fetched file is not an image');
    }

    const ext = (blob.type.split('/')[1] || 'png').split('+')[0];
    const fileRef = storageRef(storage, `tenant-logos/${tenantId}/${Date.now()}-external.${ext}`);
    await uploadBytes(fileRef, blob);
    return await getDownloadURL(fileRef);
  } catch (error) {
    // Fallback to normalized URL if cross-origin fetch/download is blocked.
    console.warn('Could not import external image to storage, using URL directly:', error);
    return normalizedUrl;
  }
};

const TenantManagementPage = ({
  unauthorizedRedirectPath = '/project-page',
  signOutRedirectPath = '/',
  headerTitle = 'Superadmin Tenant Management',
  consoleMode,
  superAdminEmails,
  currentUserEmail,
  onSignOut,
  onUnauthorized,
  onAfterSignOut,
  collectionNames,
}) => {
  const userData = getCookie('userData');
  const tenantManagementMode = useMemo(
    () => String(consoleMode || getTenantManagementMode()).toLowerCase(),
    [consoleMode]
  );
  const isCentralConsole = isCentralTenantManagementMode(tenantManagementMode);
  const resolvedCollectionNames = useMemo(() => {
    if (collectionNames && typeof collectionNames === 'object') {
      return setTenantManagementCollectionNames(collectionNames);
    }
    return getTenantManagementCollectionNames();
  }, [collectionNames]);
  const allowedSuperAdmins = useMemo(
    () => parseSuperAdminEmails(superAdminEmails),
    [superAdminEmails]
  );

  const [tenants, setTenants] = useState([]);
  const [addTenantOpen, setAddTenantOpen] = useState(false);
  const [newTenantId, setNewTenantId] = useState('');
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantStatus, setNewTenantStatus] = useState('active');
  const [selectedLicensePlan, setSelectedLicensePlan] = useState(DEFAULT_LICENSE_PLAN);
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [viewAdminsOpen, setViewAdminsOpen] = useState(false);
  const [selectedTenantForAdmin, setSelectedTenantForAdmin] = useState(null);
  const [isViewEditMode, setIsViewEditMode] = useState(false);
  const [viewTenantForm, setViewTenantForm] = useState({
    tenantName: '',
    companyName: '',
    organizationType: 'Private',
    domain: '',
    companyAddress: '',
    companyLogoUrl: '',
    personalName: '',
    personalPhoneNumber: '',
    licensePlan: DEFAULT_LICENSE_PLAN,
  });
  const [viewLogoInputType, setViewLogoInputType] = useState('url');
  const [viewLogoFile, setViewLogoFile] = useState(null);
  const [viewLogoPreviewSrc, setViewLogoPreviewSrc] = useState('');
  const [showViewLogoPreview, setShowViewLogoPreview] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [tenantAdmins, setTenantAdmins] = useState({});
  const [adminReplaceTarget, setAdminReplaceTarget] = useState(null);
  const [replacementAdminEmail, setReplacementAdminEmail] = useState('');
  const [replacementAdminName, setReplacementAdminName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [licenseActionTenant, setLicenseActionTenant] = useState(null);
  const [planChangeOpen, setPlanChangeOpen] = useState(false);
  const [planChangeValue, setPlanChangeValue] = useState(DEFAULT_LICENSE_PLAN);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [topUpCredits, setTopUpCredits] = useState('');
  const [savingLicenseAction, setSavingLicenseAction] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(true);
  
  // Admin assignment during tenant creation
  const [tenantAdminEmail, setTenantAdminEmail] = useState('');
  const [tenantAdminName, setTenantAdminName] = useState('');
  const [tenantAdminPhone, setTenantAdminPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [organizationType, setOrganizationType] = useState('Private');
  const [domain, setDomain] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [logoInputType, setLogoInputType] = useState('url');
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [logoPreviewSrc, setLogoPreviewSrc] = useState('');
  const [showLogoPreview, setShowLogoPreview] = useState(false);
  
  const navigate = useNavigate();

  const email = (currentUserEmail || userData?.email || '').toLowerCase();

  const getTenantAdminCount = (tenant) => {
    if (!tenant?.tenantId) return 0;
    return tenantAdmins[tenant.tenantId]?.length || 0;
  };

  // Allow add-admin only when tenant has zero admins.
  const canAddAdminToTenant = (tenant) => getTenantAdminCount(tenant) === 0;

  const loadTenantData = useCallback(async ({ syncFromSource = false } = {}) => {
    if (!allowedSuperAdmins.includes((email || '').toLowerCase())) {
      return false;
    }

    setLoadingTenants(true);
    try {
      if (syncFromSource && isCentralConsole) {
        const refreshResult = await refreshTenants({ mode: tenantManagementMode });
        if (refreshResult?.success === false) {
          throw new Error(refreshResult.reason || 'Failed to refresh tenant data');
        }
      }

      const fetchedTenants = await fetchTenants();
      const validTenants = (fetchedTenants || []).filter(
        (tenant) => tenant?.tenantId && tenant?.tenantName
      );
      setTenants(validTenants);

      const adminResults = await Promise.allSettled(
        validTenants.map((tenant) => fetchTenantAdmins(tenant.tenantId))
      );

      const adminsMap = {};
      validTenants.forEach((tenant, index) => {
        const result = adminResults[index];
        adminsMap[tenant.tenantId] = result?.status === 'fulfilled' ? (result.value || []) : [];
      });
      setTenantAdmins(adminsMap);
      return true;
    } catch (error) {
      console.error('Error fetching tenant data:', error);
      setSnackbar({ open: true, message: 'Could not load tenant data', severity: 'error' });
      return false;
    } finally {
      setLoadingTenants(false);
    }
  }, [allowedSuperAdmins, email, fetchTenants, fetchTenantAdmins, isCentralConsole, refreshTenants, tenantManagementMode]);

  useEffect(() => {
    if (isCentralConsole) return undefined;
    if (!allowedSuperAdmins.includes((email || '').toLowerCase())) return undefined;

    setLoadingTenants(true);
    const unsubscribe = onSnapshot(
      collection(db, 'tenant'),
      async (snapshot) => {
        const liveTenants = snapshot.docs
          .map((tenantDoc) => ({ id: tenantDoc.id, ...tenantDoc.data() }))
          .filter((tenant) => tenant?.tenantId && tenant?.tenantName);
        setTenants(liveTenants);

        const adminResults = await Promise.allSettled(
          liveTenants.map((tenant) => fetchTenantAdmins(tenant.tenantId))
        );
        const adminsMap = {};
        liveTenants.forEach((tenant, index) => {
          const result = adminResults[index];
          adminsMap[tenant.tenantId] = result?.status === 'fulfilled' ? (result.value || []) : [];
        });
        setTenantAdmins(adminsMap);
        setLoadingTenants(false);
      },
      (error) => {
        console.error('Live tenant subscription failed:', error);
        setSnackbar({ open: true, message: 'Could not start live tenant updates', severity: 'error' });
        setLoadingTenants(false);
      }
    );

    return unsubscribe;
  }, [allowedSuperAdmins, email, fetchTenantAdmins, isCentralConsole]);

  useEffect(() => {
    let objectUrl = '';
    if (logoInputType === 'upload' && companyLogoFile) {
      objectUrl = URL.createObjectURL(companyLogoFile);
      setLogoPreviewSrc(objectUrl);
    } else if (logoInputType === 'url') {
      setLogoPreviewSrc(normalizeImageUrl(companyLogoUrl));
    } else {
      setLogoPreviewSrc('');
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [logoInputType, companyLogoFile, companyLogoUrl]);

  useEffect(() => {
    let objectUrl = '';
    if (viewLogoInputType === 'upload' && viewLogoFile) {
      objectUrl = URL.createObjectURL(viewLogoFile);
      setViewLogoPreviewSrc(objectUrl);
    } else if (viewLogoInputType === 'url') {
      setViewLogoPreviewSrc(normalizeImageUrl(viewTenantForm.companyLogoUrl));
    } else {
      setViewLogoPreviewSrc('');
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [viewLogoInputType, viewLogoFile, viewTenantForm.companyLogoUrl]);

  useEffect(() => {
    if (!allowedSuperAdmins.includes((email || '').toLowerCase())) {
      // Not a superadmin, redirect to their own project page
      if (typeof onUnauthorized === 'function') {
        onUnauthorized();
      } else {
        navigate(unauthorizedRedirectPath);
      }
      return;
    }

    void loadTenantData();
  }, [email, allowedSuperAdmins, loadTenantData, navigate, onUnauthorized, unauthorizedRedirectPath]);


    // Add Tenant Handlers
  const handleAddTenantOpen = () => {
    setAddTenantOpen(true);
    setNewTenantId('');
    setNewTenantName('');
    setNewTenantStatus('active');
    setSelectedLicensePlan(DEFAULT_LICENSE_PLAN);
    setEditingTenantId(null);
    setTenantAdminEmail('');
    setTenantAdminName('');
    setTenantAdminPhone('');
    setCompanyName('');
    setOrganizationType('Private');
    setDomain('');
    setCompanyAddress('');
    setLogoInputType('url');
    setCompanyLogoUrl('');
    setCompanyLogoFile(null);
    setShowLogoPreview(false);
  };

  const handleAddTenantClose = () => {
    setAddTenantOpen(false);
  };

  const handleAddTenantSubmit = async () => {
    // Require admin assignment for new tenants
    if (
      !tenantAdminEmail.trim() ||
      !tenantAdminName.trim() ||
      !newTenantName.trim()
    ) {
      setSnackbar({ open: true, message: 'Please provide Tenant Name, Admin Email, and Personal Name', severity: 'error' });
      return;
    }
    
    try {
      let resolvedLogoUrl = logoInputType === 'url' ? companyLogoUrl.trim() : '';

      // Prefer uploaded PNG logo over URL input when provided.
      if (logoInputType === 'upload' && companyLogoFile) {
        const logoRef = storageRef(storage, `tenant-logos/${newTenantName.trim() || 'tenant'}/${Date.now()}-${companyLogoFile.name}`);
        await uploadBytes(logoRef, companyLogoFile);
        resolvedLogoUrl = await getDownloadURL(logoRef);
      } else if (logoInputType === 'url' && resolvedLogoUrl) {
        resolvedLogoUrl = await uploadExternalImageToStorage(resolvedLogoUrl, newTenantName.trim() || 'tenant');
      }

      if (isCentralConsole) {
        const result = await createTenant(
          {
            tenantName: newTenantName.trim(),
            companyName: companyName.trim(),
            organizationType,
            domain: domain.trim(),
            companyAddress: companyAddress.trim(),
            companyLogoUrl: resolvedLogoUrl,
            personalName: tenantAdminName.trim(),
            personalPhoneNumber: tenantAdminPhone.trim(),
            plan: selectedLicensePlan,
            status: 'active',
            billingStatus: 'active',
            adminAssigned: true,
            adminEmail: tenantAdminEmail.trim().toLowerCase(),
            adminName: tenantAdminName.trim(),
            adminPhone: tenantAdminPhone.trim(),
          },
          { mode: tenantManagementMode }
        );

        if (!result?.success) {
          throw new Error(result?.reason || 'Failed to create tenant');
        }

        setAddTenantOpen(false);
        setSnackbar({
          open: true,
          message: 'Tenant created in ovplan. Click Refresh / Fetch to sync CCS.',
          severity: 'success',
        });
        return;
      }

      const createdTenant = await createTenant(
        {
          tenantName: newTenantName.trim(),
          companyName: companyName.trim(),
          organizationType,
          domain: domain.trim(),
          companyAddress: companyAddress.trim(),
          companyLogoUrl: resolvedLogoUrl,
          personalName: tenantAdminName.trim(),
          personalPhoneNumber: tenantAdminPhone.trim(),
          plan: selectedLicensePlan,
          status: 'active',
          billingStatus: 'active',
          adminAssigned: true,
          adminEmail: tenantAdminEmail.trim().toLowerCase(),
          adminName: tenantAdminName.trim(),
          adminPhone: tenantAdminPhone.trim(),
        },
        { mode: tenantManagementMode }
      );

      if (!createdTenant?.success) {
        throw new Error(createdTenant?.reason || 'Failed to create tenant');
      }

      const generatedTenantId =
        createdTenant.tenantId ||
        createdTenant.tenant?.tenantId ||
        createdTenant.id ||
        createdTenant.tenant?.id;

      if (!generatedTenantId) {
        throw new Error('Tenant was created but no tenant ID was returned');
      }

      const createdTenantRecord = createdTenant.tenant || {
        id: createdTenant.id || generatedTenantId,
        tenantId: generatedTenantId,
        tenantName: newTenantName.trim(),
        companyName: companyName.trim(),
        organizationType,
        domain: domain.trim(),
        companyAddress: companyAddress.trim(),
        companyLogoUrl: resolvedLogoUrl,
        personalName: tenantAdminName.trim(),
        personalPhoneNumber: tenantAdminPhone.trim(),
        plan: selectedLicensePlan,
        status: 'active',
      };

      await addAdminToTenant(
        generatedTenantId,
        tenantAdminEmail.toLowerCase(),
        tenantAdminName.trim(),
        { tenantName: newTenantName.trim(), mode: tenantManagementMode }
      );

      const admins = await fetchTenantAdmins(generatedTenantId);
      setTenants((prev) => [...prev, {
        ...createdTenantRecord,
        id: createdTenantRecord.id || generatedTenantId,
        tenantId: generatedTenantId,
        tenantName: createdTenantRecord.tenantName || newTenantName.trim(),
        companyName: createdTenantRecord.companyName || companyName.trim(),
        organizationType: createdTenantRecord.organizationType || organizationType,
        domain: createdTenantRecord.domain || domain.trim(),
        companyAddress: createdTenantRecord.companyAddress || companyAddress.trim(),
        companyLogoUrl: createdTenantRecord.companyLogoUrl || resolvedLogoUrl,
        personalName: createdTenantRecord.personalName || tenantAdminName.trim(),
        personalPhoneNumber: createdTenantRecord.personalPhoneNumber || tenantAdminPhone.trim(),
        plan: createdTenantRecord.plan || selectedLicensePlan,
        status: createdTenantRecord.status || 'active',
        adminAssigned: true,
      }]);
      setTenantAdmins((prev) => ({
        ...prev,
        [generatedTenantId]: admins,
      }));

      setAddTenantOpen(false);
      setSnackbar({ open: true, message: `Tenant created with admin ${tenantAdminName} assigned!`, severity: 'success' });
      console.log('Tenant added successfully:', generatedTenantId);
    } catch (error) {
      console.error("Failed to add tenant:", error);
      setSnackbar({ open: true, message: 'Failed to create tenant', severity: 'error' });
    }
  };

const handleEditTenant = (tenant) => {
  if (isCentralConsole) {
    setSnackbar({
      open: true,
      message: 'Tenant profile edits are managed in ovplan.',
      severity: 'info',
    });
    return;
  }
  setEditingTenantId(tenant.id);
  setNewTenantId(tenant.tenantId);
  setNewTenantName(tenant.tenantName);
  setNewTenantStatus(tenant.status || 'active');
  setCompanyName(tenant.companyName || '');
  setOrganizationType(tenant.organizationType || 'Private');
  setDomain(tenant.domain || '');
  setCompanyAddress(tenant.companyAddress || '');
  setCompanyLogoUrl(tenant.companyLogoUrl || '');
  setCompanyLogoFile(null);
  setLogoInputType('url');
  setShowLogoPreview(false);
  setTenantAdminName(tenant.personalName || '');
  setTenantAdminPhone(tenant.personalPhoneNumber || '');
  setSelectedLicensePlan(getPlanNameFromTenant(tenant));
  setAddTenantOpen(true);
};

const handleUpdateTenantSubmit = async () => {
  if (!newTenantId.trim() || !editingTenantId) return;
  if (isCentralConsole) {
    setSnackbar({
      open: true,
      message: 'Tenant profile edits are managed in ovplan.',
      severity: 'info',
    });
    return;
  }
  try {
    const existingTenant = tenants.find((tenant) => tenant.id === editingTenantId);
    const existingPlan = existingTenant ? getPlanNameFromTenant(existingTenant) : selectedLicensePlan;
    let resolvedLogoUrl = logoInputType === 'url' ? companyLogoUrl.trim() : '';
    if (logoInputType === 'upload' && companyLogoFile) {
      const logoRef = storageRef(storage, `tenant-logos/${newTenantId}/${Date.now()}-${companyLogoFile.name}`);
      await uploadBytes(logoRef, companyLogoFile);
      resolvedLogoUrl = await getDownloadURL(logoRef);
    } else if (logoInputType === 'url' && resolvedLogoUrl) {
      resolvedLogoUrl = await uploadExternalImageToStorage(resolvedLogoUrl, newTenantId);
    }

    const tenantRef = doc(db, resolvedCollectionNames.tenants, editingTenantId);
    const nextSourceVersion = Number(existingTenant?.sourceVersion || existingTenant?.license?.syncVersion || 0) + 1;
    await updateDoc(tenantRef, {
      tenantId: newTenantId.trim(),
      tenantName: newTenantName.trim(),
      companyName: companyName.trim(),
      organizationType: organizationType,
      domain: domain.trim(),
      companyAddress: companyAddress.trim(),
      companyLogoUrl: resolvedLogoUrl,
      personalName: tenantAdminName.trim(),
      personalPhoneNumber: tenantAdminPhone.trim(),
      sourceVersion: nextSourceVersion,
      updatedAt: new Date()
    });

    let licenseUpdate = {};
    if (existingTenant && existingPlan !== selectedLicensePlan) {
      const planResult = await assignPlanToTenant(
        existingTenant.tenantId || existingTenant.id,
        selectedLicensePlan,
        { mode: tenantManagementMode }
      );
      if (!planResult.success) {
        throw new Error(planResult.reason || 'Failed to update tenant plan');
      }
      licenseUpdate = {
        plan: planResult.planId || selectedLicensePlan,
        planId: planResult.planId || selectedLicensePlan,
        license: planResult.license || existingTenant.license,
        remainingCredits: planResult.tenant?.remainingCredits,
        usedCredits: planResult.tenant?.usedCredits,
        sourceVersion: planResult.tenant?.sourceVersion,
      };
    }

    let statusUpdate = {};
    if (existingTenant && (existingTenant.status || 'active') !== newTenantStatus) {
      const statusResult = await setTenantStatus(
        existingTenant.tenantId || existingTenant.id,
        newTenantStatus,
        { mode: tenantManagementMode }
      );
      if (!statusResult.success) {
        throw new Error(statusResult.reason || 'Failed to update tenant status');
      }
      statusUpdate = {
        status: statusResult.status || newTenantStatus,
        sourceVersion: statusResult.tenant?.sourceVersion,
      };
    }

    setTenants(tenants.map(t =>
      t.id === editingTenantId
        ? {
            ...t,
            tenantId: newTenantId.trim(),
            tenantName: newTenantName.trim(),
            companyName: companyName.trim(),
            organizationType: organizationType,
            domain: domain.trim(),
            companyAddress: companyAddress.trim(),
            companyLogoUrl: resolvedLogoUrl,
            personalName: tenantAdminName.trim(),
            personalPhoneNumber: tenantAdminPhone.trim(),
            ...licenseUpdate,
            ...statusUpdate,
          }
        : t
    ));
    setAddTenantOpen(false);
    // setEditingTenantId(null);
    // setNewTenantId('');
    // setNewTenantName('');
    console.log("Tenant updated successfully:", editingTenantId);
  } catch (error) {
    console.error("Failed to update tenant:", error);
  }
};

const handleDeleteTenant = async (tenantDocId) => {
  if (isCentralConsole) {
    setSnackbar({
      open: true,
      message: 'Tenant status changes are managed in ovplan.',
      severity: 'info',
    });
    return;
  }
  if (window.confirm("Are you sure you want to disable this tenant?")) {
    try {
      const result = await setTenantStatus(tenantDocId, 'inactive', { mode: tenantManagementMode });
      if (!result.success) {
        throw new Error(result.reason || 'Failed to disable tenant');
      }
      // setTenants(tenants.filter(t => t.id !== tenantDocId));
      setTenants(tenants.map(t =>
        t.id === tenantDocId ? { ...t, status: 'inactive' } : t));
    } catch (error) {
      console.error("Failed to delete tenant:", error);
    }
  }
};

const activeTenants = tenants.filter(t => t.status === 'active');

// Admin Management Handlers
const handleAddAdminClick = async (tenant) => {
  try {
    const latestAdmins = await fetchTenantAdmins(tenant.tenantId);
    setTenantAdmins((prev) => ({ ...prev, [tenant.tenantId]: latestAdmins }));
    if ((latestAdmins || []).length > 0) {
      setSnackbar({
        open: true,
        message: 'This tenant already has admin(s). You can only add admin when a tenant has no admins.',
        severity: 'warning',
      });
      return;
    }
    setSelectedTenantForAdmin(tenant);
    setAdminEmail('');
    setAdminName('');
    setAddAdminOpen(true);
  } catch (error) {
    console.error('Error checking tenant admins before add:', error);
    setSnackbar({ open: true, message: 'Failed to verify tenant admins', severity: 'error' });
  }
};

const handleAddAdminClose = () => {
  setAddAdminOpen(false);
  setAdminEmail('');
  setAdminName('');
};

const handleViewAdminsClick = (tenant) => {
  setSelectedTenantForAdmin(tenant);
  setViewTenantForm({
    tenantName: tenant?.tenantName || '',
    companyName: tenant?.companyName || '',
    organizationType: tenant?.organizationType || 'Private',
    domain: tenant?.domain || '',
    companyAddress: tenant?.companyAddress || '',
    companyLogoUrl: tenant?.companyLogoUrl || '',
    personalName: tenant?.personalName || '',
    personalPhoneNumber: tenant?.personalPhoneNumber || '',
    licensePlan: getPlanNameFromTenant(tenant),
  });
  setViewLogoInputType('url');
  setViewLogoFile(null);
  setShowViewLogoPreview(false);
  setIsViewEditMode(false);
  setViewAdminsOpen(true);
};

const handleViewAdminsClose = () => {
  setViewAdminsOpen(false);
  setSelectedTenantForAdmin(null);
  setIsViewEditMode(false);
  setViewLogoFile(null);
  setShowViewLogoPreview(false);
  setAdminReplaceTarget(null);
  setReplacementAdminEmail('');
  setReplacementAdminName('');
};

const handleStatusToggle = async (tenant) => {
  if (isCentralConsole) {
    setSnackbar({
      open: true,
      message: 'Tenant status changes are managed in ovplan.',
      severity: 'info',
    });
    return;
  }
  try {
    const nextStatus = tenant.status === 'active' ? 'inactive' : 'active';
    const result = await setTenantStatus(tenant.tenantId || tenant.id, nextStatus, { mode: tenantManagementMode });
    if (!result.success) {
      throw new Error(result.reason || 'Failed to update tenant status');
    }
    setTenants(tenants.map(t => t.id === tenant.id ? { ...t, status: nextStatus } : t));
    if (selectedTenantForAdmin?.id === tenant.id) {
      setSelectedTenantForAdmin({ ...selectedTenantForAdmin, status: nextStatus });
    }
  } catch (error) {
    console.error("Failed to update tenant status:", error);
    setSnackbar({ open: true, message: 'Failed to update tenant status', severity: 'error' });
  }
};

const updateTenantInState = (updatedTenant) => {
  setTenants((prev) => prev.map((tenant) => (tenant.id === updatedTenant.id ? updatedTenant : tenant)));
  if (selectedTenantForAdmin?.id === updatedTenant.id) {
    setSelectedTenantForAdmin(updatedTenant);
  }
};

const handleOpenPlanChange = (tenant) => {
  setLicenseActionTenant(tenant);
  setPlanChangeValue(getPlanNameFromTenant(tenant));
  setPlanChangeOpen(true);
};

const handleClosePlanChange = () => {
  if (savingLicenseAction) return;
  setPlanChangeOpen(false);
  setLicenseActionTenant(null);
};

const handleConfirmPlanChange = async () => {
  if (!licenseActionTenant || !planChangeValue) return;
  setSavingLicenseAction(true);
  try {
    const result = await assignPlanToTenant(
      licenseActionTenant.tenantId || licenseActionTenant.id,
      planChangeValue,
      { mode: tenantManagementMode }
    );
    if (!result.success) {
      throw new Error(result.reason || 'Failed to change plan');
    }
    if (isCentralConsole) {
      setSnackbar({
        open: true,
        message: `Plan updated in ovplan. Click Refresh / Fetch to sync CCS.`,
        severity: 'success',
      });
      setPlanChangeOpen(false);
      setLicenseActionTenant(null);
      return;
    }
    const updatedTenant = {
      ...licenseActionTenant,
      plan: result.planId || planChangeValue,
      planId: result.planId || planChangeValue,
      license: result.license || licenseActionTenant.license,
      remainingCredits: result.tenant?.remainingCredits,
      usedCredits: result.tenant?.usedCredits,
      sourceVersion: result.tenant?.sourceVersion,
      updatedAt: new Date(),
    };
    updateTenantInState(updatedTenant);
    setSnackbar({ open: true, message: `Plan changed to ${PLANS[planChangeValue]?.label || planChangeValue}`, severity: 'success' });
    setPlanChangeOpen(false);
    setLicenseActionTenant(null);
  } catch (error) {
    console.error('Failed to change tenant plan:', error);
    setSnackbar({ open: true, message: 'Failed to change plan', severity: 'error' });
  } finally {
    setSavingLicenseAction(false);
  }
};

const handleOpenTopUp = (tenant) => {
  setLicenseActionTenant(tenant);
  setTopUpCredits('');
  setTopUpOpen(true);
};

const handleCloseTopUp = () => {
  if (savingLicenseAction) return;
  setTopUpOpen(false);
  setLicenseActionTenant(null);
  setTopUpCredits('');
};

const handleConfirmTopUp = async () => {
  const creditsToAdd = Math.floor(Number(topUpCredits));
  if (!licenseActionTenant || !Number.isFinite(creditsToAdd) || creditsToAdd <= 0) {
    setSnackbar({ open: true, message: 'Enter a valid credit top-up amount', severity: 'error' });
    return;
  }

  setSavingLicenseAction(true);
  try {
    const result = await addCreditsToTenant(
      licenseActionTenant.tenantId || licenseActionTenant.id,
      creditsToAdd,
      { mode: tenantManagementMode }
    );
    if (!result.success) {
      throw new Error(result.reason || 'Failed to top up credits');
    }
    if (isCentralConsole) {
      setSnackbar({
        open: true,
        message: `${formatNumber(creditsToAdd)} credits topped up in ovplan. Click Refresh / Fetch to sync CCS.`,
        severity: 'success',
      });
      setTopUpOpen(false);
      setLicenseActionTenant(null);
      setTopUpCredits('');
      return;
    }
    const license = result.license;
    const updatedTenant = {
      ...licenseActionTenant,
      plan: license?.plan || getPlanNameFromTenant(licenseActionTenant),
      license: license || licenseActionTenant.license,
      remainingCredits: result.remainingCredits,
      usedCredits: result.usedCredits,
      sourceVersion: result.sourceVersion,
      updatedAt: new Date(),
    };
    updateTenantInState(updatedTenant);
    setSnackbar({ open: true, message: `${formatNumber(creditsToAdd)} credits topped up`, severity: 'success' });
    setTopUpOpen(false);
    setLicenseActionTenant(null);
    setTopUpCredits('');
  } catch (error) {
    console.error('Failed to top up tenant credits:', error);
    setSnackbar({ open: true, message: 'Failed to top up credits', severity: 'error' });
  } finally {
    setSavingLicenseAction(false);
  }
};

const handleSaveViewEdits = async () => {
  if (!selectedTenantForAdmin) return;
  if (isCentralConsole) {
    setSnackbar({
      open: true,
      message: 'Tenant profile edits are managed in ovplan.',
      severity: 'info',
    });
    return;
  }
  try {
    let resolvedLogoUrl = viewLogoInputType === 'url'
      ? (viewTenantForm.companyLogoUrl || '').trim()
      : '';

    if (viewLogoInputType === 'upload' && viewLogoFile) {
      const logoRef = storageRef(
        storage,
        `tenant-logos/${selectedTenantForAdmin.tenantId}/${Date.now()}-${viewLogoFile.name}`
      );
      await uploadBytes(logoRef, viewLogoFile);
      resolvedLogoUrl = await getDownloadURL(logoRef);
    } else if (viewLogoInputType === 'url' && resolvedLogoUrl) {
      resolvedLogoUrl = await uploadExternalImageToStorage(
        resolvedLogoUrl,
        selectedTenantForAdmin.tenantId
      );
    }

    const payload = {
      tenantName: (viewTenantForm.tenantName || '').trim(),
      companyName: (viewTenantForm.companyName || '').trim(),
      organizationType: viewTenantForm.organizationType || 'Private',
      domain: (viewTenantForm.domain || '').trim(),
      companyAddress: (viewTenantForm.companyAddress || '').trim(),
      companyLogoUrl: resolvedLogoUrl,
      personalName: (viewTenantForm.personalName || '').trim(),
      personalPhoneNumber: (viewTenantForm.personalPhoneNumber || '').trim(),
      sourceVersion: Number(selectedTenantForAdmin?.sourceVersion || selectedTenantForAdmin?.license?.syncVersion || 0) + 1,
      updatedAt: new Date(),
    };

    await updateDoc(doc(db, resolvedCollectionNames.tenants, selectedTenantForAdmin.id), payload);

    const nextPlan = viewTenantForm.licensePlan || selectedLicensePlan;
    let licenseUpdate = {};
    if (getPlanNameFromTenant(selectedTenantForAdmin) !== nextPlan) {
      const planResult = await assignPlanToTenant(
        selectedTenantForAdmin.tenantId || selectedTenantForAdmin.id,
        nextPlan,
        { mode: tenantManagementMode }
      );
      if (!planResult.success) {
        throw new Error(planResult.reason || 'Failed to update tenant plan');
      }
      licenseUpdate = {
        plan: planResult.planId || nextPlan,
        planId: planResult.planId || nextPlan,
        license: planResult.license || selectedTenantForAdmin.license,
        remainingCredits: planResult.tenant?.remainingCredits,
        usedCredits: planResult.tenant?.usedCredits,
        sourceVersion: planResult.tenant?.sourceVersion,
      };
    }

    const updatedTenant = { ...selectedTenantForAdmin, ...payload, ...licenseUpdate };
    setSelectedTenantForAdmin(updatedTenant);
    setTenants(tenants.map((t) => (t.id === selectedTenantForAdmin.id ? updatedTenant : t)));
    setViewTenantForm({
      tenantName: updatedTenant.tenantName || '',
      companyName: updatedTenant.companyName || '',
      organizationType: updatedTenant.organizationType || 'Private',
      domain: updatedTenant.domain || '',
      companyAddress: updatedTenant.companyAddress || '',
      companyLogoUrl: updatedTenant.companyLogoUrl || '',
      personalName: updatedTenant.personalName || '',
      personalPhoneNumber: updatedTenant.personalPhoneNumber || '',
      licensePlan: getPlanNameFromTenant(updatedTenant),
    });
    setIsViewEditMode(false);
    setViewLogoFile(null);
    setShowViewLogoPreview(false);
    setSnackbar({ open: true, message: 'Tenant updated successfully', severity: 'success' });
  } catch (error) {
    console.error("Failed to save tenant changes:", error);
    setSnackbar({ open: true, message: 'Failed to save tenant changes', severity: 'error' });
  }
};

const handleAddAdminSubmit = async () => {
  if (!adminEmail.trim() || !adminName.trim() || !selectedTenantForAdmin) return;

  try {
    // DB check: allow adding only if no admin exists right now.
    const latestAdmins = await fetchTenantAdmins(selectedTenantForAdmin.tenantId);
    if ((latestAdmins || []).length > 0) {
      setTenantAdmins((prev) => ({ ...prev, [selectedTenantForAdmin.tenantId]: latestAdmins }));
      setSnackbar({
        open: true,
        message: 'Admin already exists for this tenant. Cannot add more.',
        severity: 'warning',
      });
      handleAddAdminClose();
      return;
    }

    await addAdminToTenant(
      selectedTenantForAdmin.tenantId,
      adminEmail,
      adminName,
      { tenantName: selectedTenantForAdmin.tenantName, mode: tenantManagementMode }
    );

    if (isCentralConsole) {
      setSnackbar({
        open: true,
        message: `Admin ${adminEmail} added in ovplan. Click Refresh / Fetch to sync CCS.`,
        severity: 'success',
      });
      handleAddAdminClose();
      return;
    }

    // Refresh the admins list for this tenant
    const updatedAdmins = await fetchTenantAdmins(selectedTenantForAdmin.tenantId);
    setTenantAdmins({
      ...tenantAdmins,
      [selectedTenantForAdmin.tenantId]: updatedAdmins
    });
    
    setSnackbar({ open: true, message: `Admin ${adminEmail} added successfully to ${selectedTenantForAdmin.tenantName}!`, severity: 'success' });
    handleAddAdminClose();
  } catch (error) {
    console.error("Error adding admin to tenant:", error);
    setSnackbar({ open: true, message: 'Failed to add admin to tenant', severity: 'error' });
  }
};

const handleStartRemoveAdmin = (tenantTenantId, tenantAdminId) => {
  setAdminReplaceTarget({ tenantTenantId, tenantAdminId });
  setReplacementAdminEmail('');
  setReplacementAdminName('');
};

const handleCancelRemoveAdmin = () => {
  setAdminReplaceTarget(null);
  setReplacementAdminEmail('');
  setReplacementAdminName('');
};

const handleConfirmRemoveAdmin = async (tenantTenantId, tenantAdminId) => {
  try {
    const replacementEmail = replacementAdminEmail.trim().toLowerCase();
    if (!replacementEmail) {
      setSnackbar({ open: true, message: 'Replacement admin email is required to remove current admin', severity: 'error' });
      return;
    }

    const tenantName =
      selectedTenantForAdmin?.tenantId === tenantTenantId
        ? selectedTenantForAdmin?.tenantName
        : (tenants.find((t) => t.tenantId === tenantTenantId)?.tenantName || 'Tenant');

    await removeAdminFromTenant(tenantTenantId, tenantAdminId, {
      email: replacementEmail,
      name: replacementAdminName.trim(),
      tenantName,
      mode: tenantManagementMode,
    });
    
    if (isCentralConsole) {
      setSnackbar({
        open: true,
        message: 'Admin replaced in ovplan. Click Refresh / Fetch to sync CCS.',
        severity: 'success',
      });
      setAdminReplaceTarget(null);
      setReplacementAdminEmail('');
      setReplacementAdminName('');
      return;
    }

    // Refresh the admins list for this tenant
    const updatedAdmins = await fetchTenantAdmins(tenantTenantId);
    setTenantAdmins({
      ...tenantAdmins,
      [tenantTenantId]: updatedAdmins
    });
    
    setAdminReplaceTarget(null);
    setReplacementAdminEmail('');
    setReplacementAdminName('');
    setSnackbar({ open: true, message: 'Admin replaced and old admin deleted successfully', severity: 'success' });
  } catch (error) {
    console.error("Error removing admin from tenant:", error);
    setSnackbar({ open: true, message: error?.message || 'Failed to remove admin', severity: 'error' });
  }
};

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefreshTenants = async () => {
    await loadTenantData({ syncFromSource: isCentralConsole });
  };

  const tenantDialogFieldSx = {
  mt: 1.25,
  '& .MuiInputLabel-root': {
    color: '#5f6b7a',
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#1f4fbf',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#f8fbff',
    '& fieldset': {
      borderColor: 'rgba(15, 23, 42, 0.14)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(31, 79, 191, 0.35)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1f4fbf',
      boxShadow: '0 0 0 3px rgba(31, 79, 191, 0.10)',
    },
  },
  };

  const licenseSummaryForSelectedTenant = selectedTenantForAdmin
    ? getLicenseSummary(selectedTenantForAdmin)
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f4f7fb 0%, #eef2f7 100%)',
      }}
    >
      <TenantManagementHeader
        title={headerTitle}
        signOutRedirectPath={signOutRedirectPath}
        onSignOut={onSignOut}
        onAfterSignOut={onAfterSignOut}
      />
      <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, mt: 9 }}>
        <Box sx={{ maxWidth: 1450, mx: 'auto' }}>
          <Box sx={{ mt: 1 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2.5 }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2a37' }}>
                  Tenant Management
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={isCentralConsole ? 'CCS mirror' : 'Ovplan source'}
                    color={isCentralConsole ? 'warning' : 'success'}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                  <Chip
                    size="small"
                    label={loadingTenants ? 'Loading' : 'Ready'}
                    color={loadingTenants ? 'info' : 'default'}
                    variant="outlined"
                    sx={{ fontWeight: 700 }}
                  />
                </Stack>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshTenants}
                disabled={loadingTenants}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: 170,
                }}
              >
                {isCentralConsole ? 'Refresh / Fetch' : 'Refresh'}
              </Button>
              {isCentralConsole ? (
                <Button
                  variant="contained"
                  onClick={() => navigate('/tenant-pricing-console')}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: 170,
                  }}
                >
                  Pricing Console
                </Button>
              ) : null}
            </Stack>
        <TableContainer
          component={Paper}
          sx={{
            mt: 1,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
            overflow: 'hidden',
          }}
        >
              <Table>
                <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell><strong>Tenant ID</strong></TableCell>
                    <TableCell><strong>Tenant Name</strong></TableCell>
                    <TableCell><strong>Plan</strong></TableCell>
                    <TableCell><strong>Credits</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
              {tenants.map((tenant) => (
                <TableRow
                  key={tenant.id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': { backgroundColor: '#f9fbff' },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={tenant.companyLogoUrl || ''}
                        alt={`${tenant.tenantName || 'Tenant'} logo`}
                        variant="rounded"
                        sx={{ width: 30, height: 30, bgcolor: '#dbe4f0' }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {tenant.tenantId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{tenant.tenantName}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      {(() => {
                        const planName = getPlanNameFromTenant(tenant);
                        const chipColors = getPlanChipColor(planName);
                        return (
                          <Chip
                            label={PLANS[planName]?.label || 'No Plan'}
                            size="small"
                            clickable
                            onClick={() => handleOpenPlanChange(tenant)}
                            sx={{
                              fontWeight: 800,
                              color: chipColors.color,
                              backgroundColor: chipColors.backgroundColor,
                              border: `1px solid ${chipColors.borderColor}`,
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: `${chipColors.color}33`,
                              },
                            }}
                          />
                        );
                      })()}
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenTopUp(tenant)}
                        sx={{
                          minHeight: 28,
                          px: 1.3,
                          borderRadius: 1.5,
                          fontWeight: 800,
                          fontSize: '0.72rem',
                          color: '#2f7d46',
                          borderColor: '#bfe4c8',
                          textTransform: 'uppercase',
                        }}
                      >
                        Top-up
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const summary = getLicenseSummary(tenant);
                      const totalDisplayCredits = summary.totalCredits + summary.rolloverCredits;
                      const progress = totalDisplayCredits > 0
                        ? Math.min(100, (summary.remainingCredits / totalDisplayCredits) * 100)
                        : 0;
                      return (
                        <Stack spacing={0.65} sx={{ minWidth: 190, maxWidth: 260 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatNumber(summary.remainingCredits)} total left
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                              height: 5,
                              borderRadius: 999,
                              backgroundColor: '#dbeadd',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 999,
                                backgroundColor: '#4c8b4f',
                              },
                            }}
                          />
                          {summary.rolloverCredits > 0 ? (
                            <Typography variant="caption" sx={{ color: '#d2a85d', fontWeight: 700 }}>
                              {formatNumber(summary.rolloverCredits)} rollover included
                            </Typography>
                          ) : null}
                        </Stack>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {isCentralConsole ? (
                      <Chip
                        size="small"
                        label={tenant.status}
                        color={tenant.status === 'active' ? 'success' : 'default'}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                          size="small"
                          checked={tenant.status === 'active'}
                          onChange={() => handleStatusToggle(tenant)}
                        />
                        <Typography
                          variant="body2"
                          sx={{ textTransform: 'capitalize', fontWeight: 500, color: tenant.status === 'active' ? '#1b5e20' : '#7f1d1d' }}
                        >
                          {tenant.status}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Tenant">
                        <IconButton 
                          size="small"
                          onClick={() => handleViewAdminsClick(tenant)}
                          color="primary"
                          sx={{ border: '1px solid #dbe5f5', backgroundColor: '#f4f8ff' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={canAddAdminToTenant(tenant) ? "Add Admin" : "This tenant already has admin(s)"}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleAddAdminClick(tenant)}
                            color="success"
                            disabled={!canAddAdminToTenant(tenant)}
                            sx={{ border: '1px solid #d8e9db', backgroundColor: '#f4fbf5' }}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {!isCentralConsole && (
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small"
                            onClick={() => handleDeleteTenant(tenant.id)}
                            color="error"
                            sx={{ border: '1px solid #f0d8d6', backgroundColor: '#fff6f5' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No tenants found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* <Button
        variant="outlined"
        color="secondary"
        sx={{ mt: 2, ml: 2 }}
        onClick={handleAddTenantOpen}
      >
        Add Tenant
      </Button> */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAddTenantOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200,
          boxShadow: '0 10px 20px rgba(25, 118, 210, 0.35)',
        }}
      >
        <AddIcon />
      </Fab>

      <Dialog open={planChangeOpen} onClose={handleClosePlanChange} fullWidth maxWidth="xs">
        <DialogTitle>Change Plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Update the plan for <strong>{licenseActionTenant?.tenantName || licenseActionTenant?.tenantId || 'this tenant'}</strong>.
          </Typography>
          <TextField
            margin="dense"
            label="Plan"
            fullWidth
            select
            value={planChangeValue}
            onChange={(e) => setPlanChangeValue(e.target.value)}
          >
            {LICENSE_PLAN_OPTIONS.map((planKey) => (
              <MenuItem key={planKey} value={planKey}>
                {PLANS[planKey].label} - {PLANS[planKey].credits == null ? 'Custom credits' : `${formatNumber(PLANS[planKey].credits)} credits`}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="info" sx={{ mt: 2 }}>
            Changing plan updates the base monthly credit limit. Existing top-up/rollover credits stay separate.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePlanChange} disabled={savingLicenseAction}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmPlanChange}
            disabled={savingLicenseAction}
          >
            {savingLicenseAction ? 'Updating...' : 'Update Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={topUpOpen} onClose={handleCloseTopUp} fullWidth maxWidth="xs">
        <DialogTitle>Top-up Credits</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Add extra credits to <strong>{licenseActionTenant?.tenantName || licenseActionTenant?.tenantId || 'this tenant'}</strong>. These credits appear as rollover/top-up balance in both admin and superadmin views.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Credits to add"
            fullWidth
            type="number"
            value={topUpCredits}
            onChange={(e) => setTopUpCredits(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
          {licenseActionTenant && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, backgroundColor: '#f8fbff', border: '1px solid #e2e8f0' }}>
              {(() => {
                const summary = getLicenseSummary(licenseActionTenant);
                return (
                  <Stack spacing={0.3}>
                    <Typography variant="caption" color="text.secondary">Current credits</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {formatNumber(summary.cycleRemainingCredits)} / {formatNumber(summary.totalCredits)} left
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#d2a85d', fontWeight: 700 }}>
                      +{formatNumber(summary.rolloverCredits)} rollover/top-up
                    </Typography>
                  </Stack>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTopUp} disabled={savingLicenseAction}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmTopUp}
            disabled={savingLicenseAction}
          >
            {savingLicenseAction ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Tenant Dialog */}
      <Dialog
        open={addTenantOpen}
        onClose={handleAddTenantClose}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 22px 60px rgba(15, 23, 42, 0.22)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 1,
            fontSize: { xs: '1.65rem', sm: '1.95rem' },
            fontWeight: 800,
            letterSpacing: '-0.02em',
            fontFamily: '"Segoe UI", "Poppins", "Inter", sans-serif',
            color: '#0f172a',
          }}
        >
          {editingTenantId ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1, px: 3, pb: 2 }}>
          {!editingTenantId && (
            <Typography variant="body2" sx={{ mb: 1.5, color: '#64748b' }}>
              Assign an admin while creating the tenant. Additional admins cannot be added later.
            </Typography>
          )}
          {editingTenantId && (
            <TextField
              autoFocus
              margin="dense"
              label="Tenant ID"
              fullWidth
              value={newTenantId}
              onChange={e => setNewTenantId(e.target.value)}
              disabled
              sx={tenantDialogFieldSx}
            />
          )}
            <TextField
              margin="dense"
              label="Tenant Name"
              fullWidth
              value={newTenantName}
              onChange={e => setNewTenantName(e.target.value)}
              sx={tenantDialogFieldSx}
            />
            <TextField
              margin="dense"
              label="Credit Plan"
              fullWidth
              select
              value={selectedLicensePlan}
              onChange={(e) => setSelectedLicensePlan(e.target.value)}
              sx={tenantDialogFieldSx}
            >
              {LICENSE_PLAN_OPTIONS.map((planKey) => (
                <MenuItem key={planKey} value={planKey}>
                  {PLANS[planKey].label} - {PLANS[planKey].credits == null ? 'Custom credits' : `${formatNumber(PLANS[planKey].credits)} credits`}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#f8fbff', borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.18)' }}>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                Current plan preview
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {PLANS[selectedLicensePlan]?.label || selectedLicensePlan}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {PLANS[selectedLicensePlan]?.credits == null
                  ? 'Custom credits'
                  : `${formatNumber(PLANS[selectedLicensePlan].credits)} credits • ${PLANS[selectedLicensePlan].maxUsers} users`}
              </Typography>
            </Box>
            <>
              <TextField
                margin="dense"
                label="Organization Name"
                fullWidth
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                sx={tenantDialogFieldSx}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Organization Type"
                select
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value)}
                sx={tenantDialogFieldSx}
              >
                <MenuItem value="Private">Private</MenuItem>
                <MenuItem value="Public">Public</MenuItem>
              </TextField>
              <TextField
                margin="dense"
                label="Work Domain"
                fullWidth
                value={domain}
                onChange={e => setDomain(e.target.value)}
                placeholder="example.com"
                sx={tenantDialogFieldSx}
              />
              <TextField
                margin="dense"
                label="Organization Address"
                fullWidth
                multiline
                rows={2}
                value={companyAddress}
                onChange={e => setCompanyAddress(e.target.value)}
                sx={tenantDialogFieldSx}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Logo Input Type"
                select
                value={logoInputType}
                onChange={(e) => {
                  const type = e.target.value;
                  setLogoInputType(type);
                  if (type === 'url') {
                    setCompanyLogoFile(null);
                  } else {
                    setCompanyLogoUrl('');
                  }
                }}
                sx={tenantDialogFieldSx}
              >
                <MenuItem value="url">Image URL</MenuItem>
                <MenuItem value="upload">Upload PNG</MenuItem>
              </TextField>
              {logoInputType === 'url' ? (
                <TextField
                  margin="dense"
                  label="Organization Logo URL"
                  type="url"
                  fullWidth
                  value={companyLogoUrl}
                  onChange={e => setCompanyLogoUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                  sx={tenantDialogFieldSx}
                />
              ) : (
                <>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{
                      mt: 1.25,
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 600,
                      borderColor: '#b8cdf6',
                      color: '#1f4fa8',
                      backgroundColor: 'rgba(255,255,255,0.72)',
                      '&:hover': {
                        borderColor: '#7ea6ea',
                        backgroundColor: '#ffffff',
                      },
                    }}
                  >
                    Upload Logo (PNG)
                    <input
                      type="file"
                      accept="image/png"
                      hidden
                      onChange={(e) => setCompanyLogoFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {companyLogoFile && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Selected file: {companyLogoFile.name}
                    </Typography>
                  )}
                </>
              )}
              {logoPreviewSrc && (
                <Tooltip title={showLogoPreview ? 'Hide preview' : 'Show preview'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowLogoPreview((prev) => !prev)}
                    sx={{ mt: 0.5 }}
                  >
                    {showLogoPreview ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              {showLogoPreview && logoPreviewSrc && (
                <Box sx={{ mt: 1.5 }}>
                  <Box
                    component="img"
                    src={logoPreviewSrc}
                    alt="Organization logo preview"
                    sx={{
                      mt: 0.5,
                      width: 160,
                      height: 90,
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      backgroundColor: '#fafafa'
                    }}
                  />
                </Box>
              )}
            </>
            {editingTenantId && (
              <FormControl fullWidth margin="dense" sx={tenantDialogFieldSx}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newTenantStatus}
                label="Status"
                onChange={e => setNewTenantStatus(e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          )}
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: 2, border: '1px solid rgba(148, 163, 184, 0.2)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {editingTenantId ? 'Contact Details' : 'Admin Assignment'}
            </Typography>
            {!editingTenantId && (
              <TextField
                margin="dense"
                label="Admin Email"
                type="email"
                fullWidth
                value={tenantAdminEmail}
                onChange={e => setTenantAdminEmail(e.target.value)}
                placeholder="admin@example.com"
                sx={tenantDialogFieldSx}
              />
            )}
              <TextField
                margin="dense"
                label="Personal Name"
                fullWidth
                value={tenantAdminName}
              onChange={e => setTenantAdminName(e.target.value)}
              placeholder="Full Name"
              sx={tenantDialogFieldSx}
            />
              <TextField
                margin="dense"
                label="Personal Phone Number"
                fullWidth
                value={tenantAdminPhone}
                onChange={e => setTenantAdminPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                sx={tenantDialogFieldSx}
              />
            </Box>
          </DialogContent>
        <DialogActions sx={{ px: 0, pt: 1, pb: 0 }}>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              p: 2,
              borderTop: '1px solid rgba(148, 163, 184, 0.18)',
              background: 'rgba(255,255,255,0.75)',
            }}
          >
          <Button
            onClick={handleAddTenantClose}
            sx={{
              px: 2,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: '#1f4fbf',
            }}
          >
            Cancel
          </Button>
            <Button
              onClick={editingTenantId ? handleUpdateTenantSubmit : handleAddTenantSubmit}
              disabled={
                editingTenantId
                  ? !newTenantId.trim()
                  : (
                    !newTenantName.trim() ||
                    !tenantAdminEmail.trim() ||
                    !tenantAdminName.trim()
                  )
              }
              variant="contained"
              color="primary"
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 10px 20px rgba(37, 99, 235, 0.22)',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                },
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  boxShadow: 'none',
                },
              }}
            >
            {editingTenantId ? 'Update' : 'Create Tenant'}
          </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Admin to Tenant Dialog */}
      <Dialog open={addAdminOpen} onClose={handleAddAdminClose}>
        <DialogTitle>Add Admin to {selectedTenantForAdmin?.tenantName}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Email"
            type="email"
            fullWidth
            value={adminEmail}
            onChange={e => setAdminEmail(e.target.value)}
            placeholder="admin@example.com"
          />
          <TextField
            margin="dense"
            label="Admin Name"
            fullWidth
            value={adminName}
            onChange={e => setAdminName(e.target.value)}
            placeholder="Admin Full Name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddAdminClose}>Cancel</Button>
          <Button
            onClick={handleAddAdminSubmit}
            disabled={!adminEmail.trim() || !adminName.trim()}
            variant="contained"
            color="primary"
          >
            Add Admin
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Tenant Dialog */}
      <Dialog open={viewAdminsOpen} onClose={handleViewAdminsClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Tenant Details - {selectedTenantForAdmin?.tenantName} ({selectedTenantForAdmin?.tenantId})
        </DialogTitle>
        <DialogContent>
          {isViewEditMode ? (
            <Box sx={{ mt: 1, mb: 2 }}>
              <TextField
                margin="dense"
                label="Tenant ID"
                fullWidth
                value={selectedTenantForAdmin?.tenantId || ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                margin="dense"
                label="Tenant Name"
                fullWidth
                value={viewTenantForm.tenantName}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, tenantName: e.target.value }))}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Credit Plan"
                select
                value={viewTenantForm.licensePlan || DEFAULT_LICENSE_PLAN}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, licensePlan: e.target.value }))}
              >
                {LICENSE_PLAN_OPTIONS.map((planKey) => (
                  <MenuItem key={planKey} value={planKey}>
                    {PLANS[planKey].label} - {PLANS[planKey].credits == null ? 'Custom credits' : `${formatNumber(PLANS[planKey].credits)} credits`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                margin="dense"
                label="Organization Name"
                fullWidth
                value={viewTenantForm.companyName}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, companyName: e.target.value }))}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Organization Type"
                select
                value={viewTenantForm.organizationType || 'Private'}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, organizationType: e.target.value }))}
              >
                <MenuItem value="Private">Private</MenuItem>
                <MenuItem value="Public">Public</MenuItem>
              </TextField>
              <TextField
                margin="dense"
                label="Work Domain"
                fullWidth
                value={viewTenantForm.domain}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, domain: e.target.value }))}
              />
              <TextField
                margin="dense"
                label="Organization Address"
                fullWidth
                value={viewTenantForm.companyAddress}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, companyAddress: e.target.value }))}
              />
              <TextField
                margin="dense"
                fullWidth
                label="Logo Input Type"
                select
                value={viewLogoInputType}
                onChange={(e) => {
                  const type = e.target.value;
                  setViewLogoInputType(type);
                  if (type === 'url') {
                    setViewLogoFile(null);
                  } else {
                    setViewTenantForm((prev) => ({ ...prev, companyLogoUrl: '' }));
                  }
                }}
              >
                <MenuItem value="url">Image URL</MenuItem>
                <MenuItem value="upload">Upload PNG</MenuItem>
              </TextField>
              {viewLogoInputType === 'url' ? (
                  <TextField
                    margin="dense"
                    label="Organization Logo URL"
                    fullWidth
                    value={viewTenantForm.companyLogoUrl}
                    onChange={(e) => setViewTenantForm((prev) => ({ ...prev, companyLogoUrl: e.target.value }))}
                  />
              ) : (
                <>
                  <Button
                    component="label"
                    variant="outlined"
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Upload Logo (PNG)
                    <input
                      type="file"
                      accept="image/png"
                      hidden
                      onChange={(e) => setViewLogoFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {viewLogoFile && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Selected file: {viewLogoFile.name}
                    </Typography>
                  )}
                </>
              )}
              {viewLogoPreviewSrc && (
                <Tooltip title={showViewLogoPreview ? 'Hide preview' : 'Show preview'}>
                  <IconButton
                    size="small"
                    onClick={() => setShowViewLogoPreview((prev) => !prev)}
                    sx={{ mt: 0.5 }}
                  >
                    {showViewLogoPreview ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
              {showViewLogoPreview && viewLogoPreviewSrc && (
                <Box sx={{ mt: 1.5 }}>
                  <Box
                    component="img"
                    src={viewLogoPreviewSrc}
                    alt="Organization logo preview"
                    sx={{
                      mt: 0.5,
                      width: 160,
                      height: 90,
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      backgroundColor: '#fafafa'
                    }}
                  />
                </Box>
              )}
              <TextField
                margin="dense"
                label="Personal Name"
                fullWidth
                value={viewTenantForm.personalName}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, personalName: e.target.value }))}
              />
              <TextField
                margin="dense"
                label="Personal Phone Number"
                fullWidth
                value={viewTenantForm.personalPhoneNumber}
                onChange={(e) => setViewTenantForm((prev) => ({ ...prev, personalPhoneNumber: e.target.value }))}
              />

              {licenseSummaryForSelectedTenant && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fbff',
                    border: '1px solid rgba(148, 163, 184, 0.18)',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    Credit Summary
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      Plan: <strong>{licenseSummaryForSelectedTenant.plan}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Cycle credits: <strong>{formatNumber(licenseSummaryForSelectedTenant.usedCredits)}</strong> used / <strong>{formatNumber(licenseSummaryForSelectedTenant.totalCredits)}</strong> total
                    </Typography>
                    <Typography variant="body2">
                      Remaining credits: <strong>{formatNumber(licenseSummaryForSelectedTenant.remainingCredits)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Rollover credits: <strong>{formatNumber(licenseSummaryForSelectedTenant.rolloverCredits)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      User limit: <strong>{formatNumber(licenseSummaryForSelectedTenant.userLimit)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Cycle status: <strong>{licenseSummaryForSelectedTenant.expired ? 'Expired' : 'Active'}</strong>
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 1, mb: 2, display: 'grid', gap: 1.2 }}>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Tenant ID</Typography>
                <Typography>{selectedTenantForAdmin?.tenantId || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Tenant Name</Typography>
                <Typography>{viewTenantForm.tenantName || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Organization Name</Typography>
                <Typography>{viewTenantForm.companyName || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Organization Type</Typography>
                <Typography>{viewTenantForm.organizationType || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Work Domain</Typography>
                <Typography>{viewTenantForm.domain || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Organization Address</Typography>
                <Typography>{viewTenantForm.companyAddress || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Organization Logo URL</Typography>
                <Typography sx={{ wordBreak: 'break-all' }}>{viewTenantForm.companyLogoUrl || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Personal Name</Typography>
                <Typography>{viewTenantForm.personalName || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2 }}>
                <Typography variant="caption" color="text.secondary">Personal Phone Number</Typography>
                <Typography>{viewTenantForm.personalPhoneNumber || '-'}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.2, backgroundColor: '#f8fbff' }}>
                <Typography variant="caption" color="text.secondary">Credit Plan</Typography>
                <Typography>{getPlanNameFromTenant(selectedTenantForAdmin)}</Typography>
                {licenseSummaryForSelectedTenant && (
                  <Stack spacing={0.3} sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Remaining credits: {formatNumber(licenseSummaryForSelectedTenant.remainingCredits)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current cycle: {formatNumber(licenseSummaryForSelectedTenant.totalCredits)} total
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Rollover: {formatNumber(licenseSummaryForSelectedTenant.rolloverCredits)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      User limit: {formatNumber(licenseSummaryForSelectedTenant.userLimit)}
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Assigned Admins</Typography>
          {tenantAdmins[selectedTenantForAdmin?.tenantId]?.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              {tenantAdmins[selectedTenantForAdmin?.tenantId].map(admin => (
                <React.Fragment key={admin.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 1.5,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      marginBottom: 1,
                      backgroundColor: '#f9f9f9'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {admin.adminName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {admin.adminEmail}
                      </Typography>
                    </Box>
                    <Tooltip title="Remove Admin">
                      <IconButton
                        size="small"
                        onClick={() => handleStartRemoveAdmin(selectedTenantForAdmin.tenantId, admin.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {adminReplaceTarget?.tenantTenantId === selectedTenantForAdmin?.tenantId &&
                    adminReplaceTarget?.tenantAdminId === admin.id && (
                      <Box
                        sx={{
                          mt: -0.5,
                          mb: 1.2,
                          ml: 1,
                          mr: 1,
                          p: 1.2,
                          border: '1px dashed #d9d9d9',
                          borderRadius: 1,
                          backgroundColor: '#fff'
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#555' }}>
                          Add replacement admin to delete this admin
                        </Typography>
                        <TextField
                          size="small"
                          margin="dense"
                          label="Replacement Admin Email"
                          type="email"
                          fullWidth
                          value={replacementAdminEmail}
                          onChange={(e) => setReplacementAdminEmail(e.target.value)}
                        />
                        <TextField
                          size="small"
                          margin="dense"
                          label="Replacement Admin Name (optional)"
                          fullWidth
                          value={replacementAdminName}
                          onChange={(e) => setReplacementAdminName(e.target.value)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                          <Button size="small" onClick={handleCancelRemoveAdmin}>
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleConfirmRemoveAdmin(selectedTenantForAdmin.tenantId, admin.id)}
                          >
                            Replace and Delete
                          </Button>
                        </Box>
                      </Box>
                    )}
                </React.Fragment>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mt: 2, color: '#999', textAlign: 'center' }}>
              No admins assigned to this tenant yet.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!isCentralConsole && (!isViewEditMode ? (
            <Button
              startIcon={<EditIcon />}
              onClick={() => {
                setShowViewLogoPreview(false);
                setIsViewEditMode(true);
              }}
              color="primary"
            >
              Edit Tenant
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  if (selectedTenantForAdmin) {
                    setViewTenantForm({
                      tenantName: selectedTenantForAdmin.tenantName || '',
                      companyName: selectedTenantForAdmin.companyName || '',
                      organizationType: selectedTenantForAdmin.organizationType || 'Private',
                      domain: selectedTenantForAdmin.domain || '',
                      companyAddress: selectedTenantForAdmin.companyAddress || '',
                      companyLogoUrl: selectedTenantForAdmin.companyLogoUrl || '',
                      personalName: selectedTenantForAdmin.personalName || '',
                      personalPhoneNumber: selectedTenantForAdmin.personalPhoneNumber || '',
                    });
                    setViewLogoInputType('url');
                    setViewLogoFile(null);
                    setShowViewLogoPreview(false);
                    setIsViewEditMode(false);
                  }
                }}
              >
                Cancel Edit
              </Button>
              <Button variant="contained" onClick={handleSaveViewEdits}>
                Save Changes
              </Button>
            </>
          ))}
          <Button onClick={handleViewAdminsClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
    </Box>
  );
}

export default TenantManagementPage;
